// Audit log (CHANGE-070). Every filter is in the URL, so /staff/log?action=ledger.fix is a link anyone can send. One request
// that wrote two rows shows as one line and both stay stored. A row links only when the server found a page that opens.
import { useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../../../lib'
import { Busy, Button, Err, Fresh, Page, Sources, Table, useCursor, when, type Column } from '../kit'

type Row = {
  id: string; at: string; actor_kind: string; actor_user_id: string | null; actor_email: string | null
  second_actor_id: string | null; second_actor_email: string | null; agent_grant_id: string | null; agent_name: string | null
  action: string; object_type: string; object_id: string; path: string | null; reason_code: string | null; note: string | null
  screen: string | null; journal_id: string | null; request_id: string | null; ip: string | null; before: unknown; after: unknown
  action_label: string | null; reason_words: string | null
}
type Result = { rows: Row[]; next_cursor: string | null; at: string }
type Options = { people: { email: string }[]; actions: string[]; action_labels: Record<string, string>; object_types: string[]; at: string }
type Group = { head: Row; more: Row[] }

const OBJECT_LABEL: Record<string, string> = {
  audit_log: 'Audit log', bill: 'Bill', card_application: 'Card request', company: 'Company', crm_company: 'Company', crm_person: 'Person', crm_send: 'Send',
  crm_task: 'Task', crm_touch: 'Touch', growth_alert: 'Alert', growth_switch: 'Switch', job: 'Job', journal: 'Journal', partner: 'Partner',
  platform_flag: 'App setting', redemption: 'Payout', trip: 'Trip',
}
const plainWords = (raw: string) => { const s = raw.replace(/^crm[._]/, '').replace(/[._]+/g, ' ').trim(); return s.charAt(0).toUpperCase() + s.slice(1) }
const objectLabel = (o: string) => OBJECT_LABEL[o] ?? plainWords(o)
const whoWords = (r: Row) => r.actor_email ?? (r.actor_kind === 'system' ? 'System' : plainWords(r.actor_kind))

/** Rows from one request become one line. Grouped by request, not by position, so a neighbour can't split a pair. */
export function pairUp(rows: Row[]): Group[] {
  const seen = new Map<string, Group>()
  const out: Group[] = []
  for (const r of rows) {
    const g = r.request_id ? seen.get(r.request_id) : undefined
    if (g) { g.more.push(r); continue }
    const fresh: Group = { head: r, more: [] }
    out.push(fresh)
    if (r.request_id) seen.set(r.request_id, fresh)
  }
  return out
}

/** The URL's dates are the viewer's days; the API takes instants, so a row never lands in the wrong day. */
function dayStart(day: string, addDays = 0): string {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d + addDays).toISOString()
}

/** A text filter: types freely, reaches the URL after a pause, and follows the URL when Clear empties it. */
function useUrlText(value: string, commit: (v: string) => void) {
  const [text, setText] = useState(value)
  useEffect(() => { setText(value) }, [value])
  useEffect(() => {
    if (text === value) return
    const t = setTimeout(() => commit(text.trim()), 300)
    return () => clearTimeout(t)
  }, [text])     // eslint-disable-line react-hooks/exhaustive-deps
  return [text, setText] as const
}

export function LogPage() {
  const [params, setParams] = useSearchParams()
  const get = (k: string) => params.get(k) ?? ''
  const set = (k: string, v: string) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n }, { replace: true })
  const [note, setNote] = useUrlText(get('q'), (v) => set('q', v))
  const [objectId, setObjectId] = useUrlText(get('object_id'), (v) => set('object_id', v))
  const [open, setOpen] = useState<Group | null>(null)

  const query = useMemo(() => {
    const from = params.get('from'), to = params.get('to')
    const q = new URLSearchParams()
    for (const k of ['who', 'action', 'object_type', 'object_id', 'actor_user_id', 'q']) { const v = params.get(k); if (v) q.set(k, v) }
    if (from) q.set('since', dayStart(from))
    if (to) q.set('until', dayStart(to, 1))
    return q.toString()
  }, [params])

  const options = useQuery({ queryKey: ['staff', 'audit', 'options'], queryFn: () => api<Options>('GET', '/api/staff/audit/options'), staleTime: 60_000 })
  const q = useInfiniteQuery({
    queryKey: ['staff', 'audit', query],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(query)
      if (pageParam) p.set('cursor', pageParam)
      return api<Result>('GET', `/api/staff/audit?${p}`)
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    refetchInterval: (s) => ((s.state.data?.pages.length ?? 1) > 1 ? false : 30_000),
  })
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.rows) ?? [], [q.data])
  const actionLabel = (a: string) => options.data?.action_labels[a] ?? rows.find((r) => r.action === a)?.action_label ?? plainWords(a)
  const groups = useMemo(() => pairUp(rows), [rows])
  const { cur } = useCursor(groups, (g) => g.head.id, setOpen)
  const readAt = q.data?.pages[q.data.pages.length - 1].at
  const filtered = ['who', 'action', 'object_type', 'object_id', 'actor_user_id', 'q', 'from', 'to'].some((k) => params.get(k))

  const pick = (k: string, list: string[]) => { const v = get(k); return v && !list.includes(v) ? [v, ...list] : list }
  const columns: Column<Group>[] = [
    { key: 'at', head: 'At', nowrap: true, cell: (g) => <span data-testid="staff-log-row">{when(g.head.at)}</span> },
    { key: 'who', head: 'Who', primary: true, cell: ({ head: r }) => (
      <div>
        {whoWords(r)}{r.second_actor_email ? ` + ${r.second_actor_email}` : ''}
        {r.agent_grant_id ? <div className="st-tl-sub" data-testid="staff-log-agent">Token: {r.agent_name ?? 'name not kept'}</div> : null}
      </div>
    ) },
    { key: 'action', head: 'Action', cell: ({ head: r, more }) => (
      <div>
        <span title={r.action}>{actionLabel(r.action)}</span>
        {more.length ? <div className="st-tl-sub" data-testid="staff-log-pair" title={more.map((m) => m.action).join(', ')}>
          {more.every((m) => m.action === r.action) ? `Logged ${more.length + 1} times by one request` : `Also logged: ${[...new Set(more.map((m) => actionLabel(m.action)))].join(', ')}`}
        </div> : null}
      </div>
    ) },
    { key: 'object', head: 'Object', cell: ({ head: r }) => (
      <span title={r.object_id}>
        {r.path ? <Link className="st-link" to={r.path} data-testid="staff-log-link">{objectLabel(r.object_type)}</Link> : objectLabel(r.object_type)}
      </span>
    ) },
    { key: 'reason', head: 'Reason', cell: ({ head: r }) => <span title={r.reason_code ?? undefined} data-testid="staff-log-reason">{r.reason_words}</span> },
    { key: 'note', head: 'Note', cell: ({ head: r }) => (r.note === r.reason_words ? null : r.note) },
    { key: 'ip', head: 'IP', nowrap: true, cell: ({ head: r }) => r.ip },
  ]

  return (
    <Page title="Audit log" testid="staff-log" right={<a className="st-btn" href={`/api/staff/audit?${new URLSearchParams([...new URLSearchParams(query), ['format', 'csv']])}`} data-testid="staff-log-csv">Export CSV</a>}>
      <div className="st-filters" data-testid="staff-log-filters">
        <select className="st-select" aria-label="Person" value={get('who')} onChange={(e) => set('who', e.target.value)} data-testid="staff-log-actor_user_id">
          <option value="">Anyone</option>
          {pick('who', (options.data?.people ?? []).map((p) => p.email)).map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <select className="st-select" aria-label="Action" value={get('action')} onChange={(e) => set('action', e.target.value)} data-testid="staff-log-action">
          <option value="">Every action</option>
          {pick('action', options.data?.actions ?? []).map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
        </select>
        <select className="st-select" aria-label="Object type" value={get('object_type')} onChange={(e) => set('object_type', e.target.value)} data-testid="staff-log-object_type">
          <option value="">Every object</option>
          {pick('object_type', options.data?.object_types ?? []).map((o) => <option key={o} value={o}>{objectLabel(o)}</option>)}
        </select>
        <input className="st-input" type="date" aria-label="From" value={get('from')} onChange={(e) => set('from', e.target.value)} data-testid="staff-log-from" />
        <input className="st-input" type="date" aria-label="To" value={get('to')} onChange={(e) => set('to', e.target.value)} data-testid="staff-log-to" />
        <input className="st-input" placeholder="Note contains" aria-label="Note contains" value={note} onChange={(e) => setNote(e.target.value)} data-testid="staff-log-q" />
        <input className="st-input st-sys-id" placeholder="Object id" aria-label="Object id" value={objectId} onChange={(e) => setObjectId(e.target.value)} data-testid="staff-log-object_id" />
        {filtered ? <Button tone="quiet" onClick={() => setParams(new URLSearchParams(), { replace: true })} data-testid="staff-log-clear">Clear</Button> : null}
      </div>
      {get('actor_user_id') ? <p className="st-note">One person, by id</p> : null}
      {readAt ? <Sources><Fresh at={readAt} live label="Log" limit={120} testid="staff-log-fresh" /></Sources> : null}
      {q.isLoading ? <Busy /> : !q.data ? <Err error={q.error} /> : (
        <>
          {q.error ? <Err error={q.error} /> : null}
          <Table testid="staff-log-table" rows={groups} rowKey={(g) => g.head.id} columns={columns} cur={cur} onOpen={setOpen} empty={filtered ? 'No entries match' : 'No entries'} />
          {q.hasNextPage ? <div><Button tone="quiet" disabled={q.isFetchingNextPage} onClick={() => q.fetchNextPage()} data-testid="staff-log-more">More</Button></div> : null}
        </>
      )}
      {open ? <Detail group={open} label={actionLabel} close={() => setOpen(null)} /> : null}
    </Page>
  )
}

function Detail({ group, label, close }: { group: Group; label: (a: string) => string; close: () => void }) {
  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [close])
  const all = [group.head, ...group.more]
  return (
    <div className="st-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) close() }}>
      <div className="st-dialog st-sys-dialog" role="dialog" aria-modal="true" aria-label="Audit entry" data-testid="staff-log-detail">
        {all.map((r) => (
          <div key={r.id} className="st-stack">
            <h2 title={r.action}>{label(r.action)}</h2>
            <dl className="st-facts">
              <dt>At</dt><dd>{when(r.at)}</dd>
              <dt>Raw action</dt><dd>{r.action}</dd>
              <dt>Who</dt><dd>{whoWords(r)}{r.second_actor_email ? ` + ${r.second_actor_email}` : ''}</dd>
              {r.agent_grant_id ? <><dt>Token</dt><dd>{r.agent_name ?? 'name not kept'}</dd></> : null}
              {r.ip ? <><dt>IP</dt><dd>{r.ip}</dd></> : null}
              <dt>Object</dt><dd>{objectLabel(r.object_type)} {r.object_id}</dd>
              {r.reason_words ? <><dt>Reason</dt><dd>{r.reason_words}</dd></> : null}
              {r.note ? <><dt>Note</dt><dd>{r.note}</dd></> : null}
              {r.screen ? <><dt>Screen</dt><dd>{r.screen}</dd></> : null}
              {r.journal_id ? <><dt>Journal</dt><dd>{r.journal_id}</dd></> : null}
              {r.request_id ? <><dt>Request</dt><dd>{r.request_id}</dd></> : null}
            </dl>
            {r.before ? <><span className="st-muted">Before</span><pre className="st-sys-pre">{JSON.stringify(r.before, null, 2)}</pre></> : null}
            {r.after ? <><span className="st-muted">After</span><pre className="st-sys-pre">{JSON.stringify(r.after, null, 2)}</pre></> : null}
          </div>
        ))}
        <div className="st-dialog-foot"><Button onClick={close} data-testid="staff-log-detail-close">Close</Button></div>
      </div>
    </div>
  )
}
